import { useState, useCallback } from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Stack,
  Button,
  Checkbox,
  TextField,
  Typography,
  Autocomplete,
  AutocompleteRenderOptionState
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { DocumentType } from 'src/services/files/file.types';
import { ICandidateProfileDocument } from 'src/services/candidates/candidates.types';
import {
  useGetProfileDocumentsQuery
} from 'src/services/candidates/candidates.service';

import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import UploadCandidateDocumentModal from 'src/sections/profile/modals/upload-document-modal';

type DocumentSelectorProps = {
  type?: DocumentType | undefined;
  profileId: string;
  label?: string;
  enableUpload?: boolean;
  onSelect: (type: DocumentType, document: ICandidateProfileDocument | null) => void;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export function ProfileDocumentSelector({ type = undefined, onSelect, profileId, enableUpload = true, label }: DocumentSelectorProps) {
  const { t } = useTranslate();

  const [searchQuery, setSearchQuery] = useState<string>('');

  const showUploadModal = useBoolean();

  const { currentData: myDocuments } = useGetProfileDocumentsQuery({
    profileId,
    params: {
      page: 1,
      per_page: 5,
      search_query: searchQuery,
      type,
    },
  });

  const [selectedDocument, setSelectedDocument] = useState<ICandidateProfileDocument | null>(null);

  const onDocumentAttached = (profileDoc: any) => {
    setSelectedDocument(profileDoc);
    onSelect(profileDoc.document.type, profileDoc);
    showUploadModal.onFalse();
  }

  const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: ICandidateProfileDocument, { selected }: AutocompleteRenderOptionState) => (
    <li {...props}>
      <Stack direction="row">
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{ marginRight: 8 }}
          checked={selectedDocument?.id === option.id}
          disabled={type === option.document.type}
        />
        <Stack direction="column" justifyContent="start" alignItems="start">
          <TruncatedTextField
            text={option.document.name}
            variant="body2"
            limit={50}
            mode='characters'
            showMoreEnabled={false}
          />

          <Typography variant="caption" color="text.secondary">
            {t(`enums.document_type.${option.document.type}`)}
          </Typography>
        </Stack>
      </Stack>
    </li>
  ), [type, t, selectedDocument]);

  return (
    <Stack direction="row" alignItems="center" gap={1} width="100%">
      {
        enableUpload && (
          <Button variant="text" onClick={showUploadModal.onTrue}>
            {t('common.upload')} {t('common.new')}
          </Button>
        )
      }

      <Autocomplete
        sx={{
          minWidth: 200,
          flexGrow: 1
        }}
        value={selectedDocument}
        onInputChange={(_e, value) => setSearchQuery(value)}
        onChange={(event, newValue) => {
          setSelectedDocument(newValue);
          onSelect(newValue?.document.type as DocumentType, newValue ?? null);
        }}
        options={myDocuments?.results ?? []}
        getOptionLabel={(option: ICandidateProfileDocument) => option.document.name as string}
        getOptionDisabled={(option) => type !== option.document.type}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? t('applications.resource_requests.action.select_documents')}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off'
            }}
          />
        )}
      />

      {
        showUploadModal.value && (
          <UploadCandidateDocumentModal
            open={showUploadModal.value}
            onClose={showUploadModal.onFalse}
            profileId={profileId}
            type={type}
            onUpload={onDocumentAttached}
          />
        )
      }
    </Stack>
  );
}